import { useCallback, useEffect, useRef, useState } from "react";
import { BellRing, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { useAdminApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const waPhone = (p) => (p.length === 10 ? `91${p}` : p);
const until = (m) => (m <= 0 ? "starting now" : m < 60 ? `in ${m} min` : `in ${Math.floor(m / 60)} h ${m % 60} min`);
const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export default function RemindersPanel() {
  const call = useAdminApi();
  const { settings } = useApp();
  const [items, setItems] = useState([]);
  const announced = useRef(new Set());

  const refresh = useCallback(() => {
    call("get", "/admin/reminders").then((list) => {
      setItems(list);
      list.filter((b) => b.due && !b.reminder_sent && !announced.current.has(b.id)).forEach((b) => {
        announced.current.add(b.id);
        toast.message(`Reminder due: ${b.name} at ${b.time}`);
      });
    });
  }, [call]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  const markSent = async (b) => {
    await call("patch", `/admin/bookings/${b.id}`, { reminder_sent: true });
    refresh();
  };

  const link = (b) => {
    const tpl = settings?.texts?.[`reminder_${b.lang}`] || settings?.texts?.reminder_en || "";
    return `https://wa.me/${waPhone(b.phone)}?text=${encodeURIComponent(tpl.replace("{name}", b.name).replace("{time}", to12h(b.time)))}`;
  };

  return (
    <div className="rounded-2xl border border-amber-200/20 bg-amber-200/[0.04] p-4" data-testid="reminders-panel">
      <p className="mb-3 flex items-center gap-2 font-display text-lg text-white"><BellRing className="h-5 w-5 text-amber-200" /> Reminders (next 24 hours)</p>
      {items.length === 0 && <p className="text-sm text-emerald-100/60" data-testid="reminders-empty">No upcoming consultations in the next 24 hours.</p>}
      <ul className="space-y-2">
        {items.map((b) => (
          <li key={b.id} data-testid={`reminder-row-${b.ref}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl p-3 ${b.due && !b.reminder_sent ? "bg-amber-300/15 ring-1 ring-amber-300/50" : "bg-black/20"}`}>
            <span className="text-sm">
              <span className="text-white">{b.name}</span> · {b.date} {b.time} · <span className="text-emerald-100/60">{until(b.minutes_to_start)}</span>
              {b.due && !b.reminder_sent && <span className="ml-2 rounded-full bg-amber-300 px-2 py-0.5 text-xs text-emerald-950">Send now</span>}
            </span>
            {b.reminder_sent ? (
              <span className="flex items-center gap-1 text-xs text-emerald-300" data-testid={`reminder-sent-${b.ref}`}><Check className="h-4 w-4" /> Reminder sent</span>
            ) : !b.due ? (
              <span className="text-xs text-emerald-100/50" data-testid={`reminder-wait-${b.ref}`}>Reminder opens 1 hour before</span>
            ) : (
              <a href={link(b)} target="_blank" rel="noreferrer" onClick={() => markSent(b)} data-testid={`reminder-send-${b.ref}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-1.5 text-sm text-[#06120e] hover:bg-[#3ee07c]">
                <Send className="h-4 w-4" /> WhatsApp reminder
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
